<template>
  <AppPopup v-model="open">
    <div class="flex flex-col m-5 mt-7 w-96">
      <div class="font-medium mb-3">Select an account</div>
      <fieldset aria-label="Server size" class="max-h-80 overflow-y-auto pl-1 pr-2 py-2 custom-srollbar">
        <RadioGroup v-model="selected_account" class="space-y-4">
          <RadioGroupOption
            v-for="account in all_accounts"
            :key="account.name"
            v-slot="{ checked }"
            as="template"
            :value="account"
            :aria-label="account.name"
            :aria-description="`${account.tapi_account_id}`"
          >
            <div
              :class="[
                checked ? 'border-operacolor ring-1 ring-operacolor' : 'border-gray-300',
                'relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between',
              ]"
            >
              <span class="flex items-center">
                <span class="flex flex-col text-sm">
                  <span class="font-medium text-gray-900">{{ account.name }}</span>
                  <span class="text-gray-500">
                    <span class="block sm:inline">{{ account.tapi_account_id }}</span>
                  </span>
                </span>
              </span>
              <span class="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                <span :class="getBadgeClass(account.type)">{{ capitalizeFirstLetter(account.type) }}</span>
                <span v-if="!account.loaded_balance" class="bg-gray-200 rounded-md w-10 h-4 animate-pulse self-end mt-0.5"></span>
                <span v-else class="mr-0.5 mt-0.5 text-xs text-gray-500">{{ dollarize(account.balance, account.balance < 99) }}</span>
              </span>
            </div>
          </RadioGroupOption>
        </RadioGroup>
      </fieldset>

      <!-- Divider -->
      <div class="relative my-8">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-center">
          <span class="bg-white px-2 text-sm text-gray-500">Actions</span>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!hasSelectedAccount" class="h-[25rem]">
        <div class="flex flex-col items-center mt-14">
          <FolderOpenIcon class="size-12 text-gray-400" />
          <CursorArrowRaysIcon class="size-8 -mt-5 ml-6 bg-white text-gray-400" />
          <h3 class="mt-2 text-sm font-semibold text-gray-900 dark:text-neutral-300">Select an account</h3>
          <p class="mt-2 text-sm text-center text-gray-500 max-w-52">Select an account to see what actions are available</p>
        </div>
      </div>
      <div v-else :class="[hasSelectedAccount ? '' : 'grayscale opacity-40 cursor-not-allowed']">
        <div class="flex items-end justify-end gap-4">
          <Transition
            mode="out-in"
            enter-active-class="transition-all duration-200 ease-in-out"
            enter-from-class="opacity-0 translate-y-6"
            enter-to-class="opacity-100 -translate-y-0"
            leave-active-class="transition-all duration-200 ease-in-out"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-6"
          >
            <AppSelect
              v-if="selected_account?.display_groups?.length > 0"
              v-model="selected_display_group"
              label="Select Display Group"
              info="This is the group that your account will appear under when viewed on your accounts dashboard"
              :items="selected_account.display_groups"
              class="flex-1"
            />
          </Transition>
          <AppButtonPrimary
            text="Rename Account"
            size="lg"
            :icon="PencilSquareIcon"
            class="w-fit"
            :disabled="!hasSelectedAccount"
            :class="[selected_account?.display_groups?.length <= 0 ? '' : 'mt-8']"
            @click="showRenameAccount = true"
          />
          <AppPopup v-model="showRenameAccount">
            <div class="flex flex-col gap-4 p-5 w-72">
              <div class="flex flex-col gap-2 items-center justify-center">
                <div
                  class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto dark:bg-blue-950 bg-gradient-to-b bg-gradient from-blue-400 to-blue-700 rounded-full sm:mx-0 sm:h-10 sm:w-10"
                >
                  <PencilSquareIcon class="w-6 h-6 text-white" aria-hidden="true" />
                </div>
                <div class="font-semibold text-lg text-gray-900 mb-2">Rename Account</div>
              </div>
              <div class="text-sm text-gray-700 text-center mb-2">
                Enter the new nickname for <span class="font-semibold text-md text-gray-900 whitespace-nowrap">{{ selected_account.name }}</span>
              </div>
              <AppInput
                id="new-nickname"
                v-model="new_nickname"
                label="New Nickname"
                placeholder="Enter a new nickname"
                tooltip="This will be the new name displayed on you dashboard for this account"
              />
              <div class="flex gap-4 justify-between mt-5">
                <AppButtonSecondary text="Cancel" size="lg" @click="showRenameAccount = false" />
                <AppButtonPrimary text="Update" size="lg" :icon="CloudArrowUpIcon" :loading="updating_nickname" @click="updateNickname" />
              </div>
            </div>
          </AppPopup>
        </div>
        <div class="bg-gray-50 p-5 rounded-lg shadow-inner mt-5">
          <fieldset>
            <legend class="flex justify-between w-full text-base font-semibold text-gray-900">
              <div>Groups</div>
              <AppButtonSecondary
                text="New"
                :icon-first="true"
                :icon="PlusCircleIcon"
                size="md"
                :disabled="!hasSelectedAccount"
                @click="showNewGroup = true"
              />
            </legend>
            <div class="h-60 flex flex-col gap-4 justify-between">
              <div
                v-if="userStore.user?.account_display_groups?.length > 0"
                class="mt-4 divide-y divide-gray-200 border-t border-gray-200 overflow-y-auto scrollbar-hide"
              >
                <div v-for="group in displayGroupsFormatted" :key="group.id" class="relative flex gap-3 py-4">
                  <div class="min-w-0 flex-1 text-sm/6">
                    <label :for="`group-${group.id}`" class="select-none font-medium text-gray-900">{{ group.id }}</label>
                  </div>
                  <div class="flex h-6 shrink-0 items-center mr-1">
                    <div class="group grid size-4 grid-cols-1">
                      <input
                        v-model="group.checked"
                        type="checkbox"
                        class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-operacolor checked:bg-operacolor indeterminate:border-operacolor indeterminate:bg-operacolor focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-operacolor disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                        @change="handleDisplayGroupsChange()"
                      />
                      <svg
                        class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          class="opacity-0 group-has-[:checked]:opacity-100"
                          d="M3 8L6 11L11 3.5"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          class="opacity-0 group-has-[:indeterminate]:opacity-100"
                          d="M3 7H11"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="showNewGroup" class="flex items-end gap-4 mt-2">
                <AppInput id="new-group-name" v-model="new_group" label="New Group Name" placeholder="Enter a group name" class="flex-1" />
                <AppButtonPrimary text="Create" size="lg" :loading="creating_new_group" @click="createNewGroup" />
                <AppButtonSecondary text="Cancel" size="lg" @click="showNewGroup = false" />
              </div>
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  </AppPopup>
</template>

<script setup>
import { ref, onMounted, computed, watchEffect, watch } from "vue";

// Stores
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();
import { useUserStore } from "@/stores/user";
const userStore = useUserStore();

// Components
import AppPopup from "@/components/applicationui/AppPopup.vue";
import AppButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";
import AppButtonSecondary from "@/components/applicationui/AppButtonSecondary.vue";
import AppSelect from "@/components/applicationinputs/AppSelect.vue";
import AppInput from "@/components/applicationinputs/AppInput.vue";

// Icons
import { CloudArrowUpIcon, PencilSquareIcon, PlusCircleIcon, FolderOpenIcon, CursorArrowRaysIcon } from "@heroicons/vue/24/outline";

import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import { getBadgeClass, capitalizeFirstLetter } from "@/helper/helper";
import { dollarize } from "@/helper/format";

// Model
const open = defineModel({
  type: Boolean,
  required: true,
});

// Accounts Start
const all_accounts = ref([]);
const selected_account = ref();
const hasSelectedAccount = computed(() => {
  if (!selected_account.value) return false;
  return true;
});
async function setAllAccounts() {
  await accountsStore.setMyAccounts();
  all_accounts.value = accountsStore.myAccounts;
}
async function getAccountBalance(account) {
  return await accountsStore.getAccountBalance(account.id);
}
async function getAllAccountsBalances() {
  all_accounts.value.forEach(async (account) => {
    account.balance = await getAccountBalance(account);
    account.loaded_balance = true;
  });
}
// Accounts End

// Groups START
const showNewGroup = ref(false);
const selected_display_group = ref();
const creating_new_group = ref(false);
const new_group = ref("");
const displayGroupsFormatted = ref([]);
watchEffect(() => {
  if (!selected_account.value) {
    displayGroupsFormatted.value = [];
    return;
  }

  if (userStore.user.account_display_groups) {
    displayGroupsFormatted.value = userStore.user.account_display_groups.map((group) => ({
      id: group,
      checked: selected_account.value.display_groups?.includes(group),
    }));
  }

  if (selected_account.value.display_group) {
    selected_display_group.value = selected_account.value.display_group;
  } else {
    selected_display_group.value = selected_account.value.display_groups?.[0];
  }
});
async function createNewGroup() {
  if (!new_group.value) return;
  creating_new_group.value = true;

  if (userStore.user.account_display_groups?.includes(new_group.value)) {
    creating_new_group.value = false;
    showNewGroup.value = false;
    return;
  }

  const updated_user_display_groups = [...(userStore.user.account_display_groups || []), new_group.value];

  await accountsStore.createNewAccountDisplayGroup(userStore.user.id, updated_user_display_groups);
  new_group.value = "";

  await userStore.setUser();
  creating_new_group.value = false;
  showNewGroup.value = false;
}
async function handleDisplayGroupsChange() {
  selected_account.value.display_groups = displayGroupsFormatted.value.filter((group) => group.checked).map((group) => group.id);

  if (!selected_account.value.display_groups || selected_account.value.display_groups.length <= 0) {
    accountsStore.updateAccountDisplayGroups(selected_account.value.id, []);
    accountsStore.updateAccountDisplayGroup(selected_account.value.id, null);
    return;
  } else {
    accountsStore.updateAccountDisplayGroups(selected_account.value.id, selected_account.value.display_groups);
    if (!selected_account.value.display_group || !selected_account.value.display_groups.includes(selected_account.value.display_group)) {
      selected_account.value.display_group = selected_account.value.display_groups[0];
      accountsStore.updateAccountDisplayGroup(selected_account.value.id, selected_account.value.display_group);
    }
  }
}
// Groups END

// Rename Account START
const showRenameAccount = ref(false);
const new_nickname = ref("");
const updating_nickname = ref(false);
async function updateNickname() {
  if (!new_nickname.value) {
    showRenameAccount.value = false;
    return;
  }
  updating_nickname.value = true;

  selected_account.value.name = new_nickname.value;
  await accountsStore.updateAccountName(selected_account.value.id, new_nickname.value);
  new_nickname.value = "";

  updating_nickname.value = false;
  showRenameAccount.value = false;
}
// Rename Account END

// Mounted START
const loading = ref(true);
onMounted(async () => {
  // Get all accounts
  await setAllAccounts();
  getAllAccountsBalances();

  // Ready
  loading.value = false;
});
// Mounted END

// Watchers
watch(selected_display_group, () => {
  if (!selected_display_group.value) return;
  if (selected_display_group.value != selected_account.value.display_group) {
    selected_account.value.display_group = selected_display_group.value;
    accountsStore.updateAccountDisplayGroup(selected_account.value.id, selected_account.value.display_group);
  }
});
watch(open, () => {
  if (!open.value) {
    selected_account.value = null;
  }
});
</script>
